import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import './App.css';

function App() {
  const [data, setData] = useState({ groups: [], allCircles: [], categories: [], appCategoryStyles: {} });
  const [activeCircles, setActiveCircles] = useState([]);
  const [activeAddOns, setActiveAddOns] = useState([]);
  const [imageUrl, setImageUrl] = useState('');
  const [activeGroupId, setActiveGroupId] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentActiveColor, setCurrentActiveColor] = useState('#ffffff');
  const [viewMode, setViewMode] = useState('ecosystem');
  const [diagramImageUrl, setDiagramImageUrl] = useState('');
  const [isPlatformViewActive, setIsPlatformViewActive] = useState(false);

  useEffect(() => {
    fetch('/data.json')
      .then(response => response.json())
      .then(jsonData => setData(jsonData));
  }, []);

  const sortedCircles = useMemo(() => {
    const { allCircles, categories } = data;
    if (!allCircles || allCircles.length === 0 || !categories || categories.length === 0) {
      return allCircles || [];
    }
    const categoryOrder = categories.reduce((acc, cat, index) => {
      acc[cat.name] = index;
      return acc;
    }, {});
    return [...allCircles].sort((a, b) => {
      const orderA = categoryOrder[a.category] ?? Infinity;
      const orderB = categoryOrder[b.category] ?? Infinity;
      return orderA - orderB;
    });
  }, [data]);

  const circlePositions = useMemo(() => {
    const positions = new Map();
    const numCircles = sortedCircles.length;
    if (numCircles === 0) return positions;
    const radius = 350;
    sortedCircles.forEach((circle, index) => {
      const angle = - (Math.PI / 2) + (index / numCircles) * 2 * Math.PI;
      const x = Math.cos(angle) * radius;
      const y = Math.sin(angle) * radius;
      positions.set(circle.label, { x, y });
    });
    return positions;
  }, [sortedCircles]);

  const circleLabelToIdMap = useMemo(() => {
    return new Map(data.allCircles.map(circle => [circle.label, circle.id]));
  }, [data.allCircles]);

  useEffect(() => {
    const activeCircleIds = new Set(activeCircles.map(label => circleLabelToIdMap.get(label)));
    const matchedGroup = data.groups.find(group => {
      if (activeCircleIds.size !== group.circles.length) return false;
      const groupCircleIds = new Set(group.circles);
      return [...activeCircleIds].every(id => groupCircleIds.has(id));
    });

    if (matchedGroup) {
      const groupCategory = matchedGroup.appCategory;
      const color = data.appCategoryStyles?.[groupCategory]?.activeColor || '#ffffff';
      setCurrentActiveColor(color);
      setImageUrl(matchedGroup.image);
      setActiveGroupId(matchedGroup.id);
      setSelectedProduct(null);
    } else if (activeCircles.length === 1) {
      setCurrentActiveColor('#ffffff');
      setActiveAddOns([]);
      const activeCircleLabel = activeCircles[0];
      const matchedCircle = sortedCircles.find(c => c.label === activeCircleLabel);
      setImageUrl('');
      if (matchedCircle) {
        setSelectedProduct(matchedCircle);
      } else {
        setSelectedProduct(null);
      }
      setActiveGroupId(null);
    } else {
      setCurrentActiveColor('#ffffff');
      setActiveAddOns([]);
      setImageUrl('');
      setActiveGroupId(null);
      setSelectedProduct(null);
    }
  }, [activeCircles, data.groups, data.allCircles, data.appCategoryStyles, sortedCircles, circleLabelToIdMap]);

  const handleCircleClick = (label) => {
    setViewMode('ecosystem');
    setActiveCircles(prev => {
      if (prev.length === 1 && prev[0] === label) {
        return [];
      }
      return [label];
    });
  };

  const handleGroupClick = (group) => {
    const circleIdToLabelMap = new Map(data.allCircles.map(circle => [circle.id, circle.label]));
    const primaryLabels = group.circles.map(id => circleIdToLabelMap.get(id)).filter(Boolean);
    const addOnLabels = (group['add-ons'] || []).map(id => circleIdToLabelMap.get(id)).filter(Boolean);
    setViewMode('ecosystem');
    setActiveCircles(primaryLabels);
    setActiveAddOns(addOnLabels);
  };

  const handleReset = () => {
    setViewMode('ecosystem');
    setActiveCircles([]);
  };

  const handleToggleView = (group) => {
    const circleIdToLabelMap = new Map(data.allCircles.map(circle => [circle.id, circle.label]));
    const primaryLabels = group.circles.map(id => circleIdToLabelMap.get(id)).filter(Boolean);
    const addOnLabels = (group['add-ons'] || []).map(id => circleIdToLabelMap.get(id)).filter(Boolean);
    
    if (viewMode === 'diagram' && diagramImageUrl === group.diagramImage) {
      setViewMode('ecosystem');
      setDiagramImageUrl('');
    } else {
      setViewMode('diagram');
      setDiagramImageUrl(group.diagramImage);
      setActiveCircles(primaryLabels);
      setActiveAddOns(addOnLabels);
      setIsPlatformViewActive(false);
    }
  };

  const handlePlatformToggle = () => {
    setIsPlatformViewActive(prev => !prev);
    setViewMode('ecosystem');
  };

  // MODIFIED: This filter now checks both the primary `circles` array and the `add-ons` array.
  const displayedGroups = selectedProduct
    ? data.groups.filter(group => {
        const isPrimary = group.circles.includes(selectedProduct.id);
        const isAddOn = (group['add-ons'] || []).includes(selectedProduct.id);
        return isPrimary || isAddOn;
      })
    : data.groups;

  return (
    <div className="app-container">
      <Sidebar
        groups={displayedGroups}
        onGroupClick={handleGroupClick}
        onResetClick={handleReset}
        activeGroupId={activeGroupId}
        selectedProduct={selectedProduct}
        onToggleView={handleToggleView}
        viewMode={viewMode}
        allCircles={sortedCircles}
      />
      <MainContent
        circles={sortedCircles}
        categories={data.categories}
        activeColor={currentActiveColor}
        activeCircles={activeCircles}
        activeAddOns={activeAddOns}
        onCircleClick={handleCircleClick}
        imageUrl={imageUrl}
        viewMode={viewMode}
        diagramImageUrl={diagramImageUrl}
        isPlatformViewActive={isPlatformViewActive}
        onPlatformToggle={handlePlatformToggle}
        platformImage={data.platformImage}
        circlePositions={circlePositions}
        selectedProduct={selectedProduct}
      />
    </div>
  );
}

export default App;